<template>
  <div class="intervention-check">
    <div
      class="
        location-select-input-group
        el-input-group el-input-group--append el-input-group--prepend
      "
    >
      <div class="el-input-group__prepend">
        <div class="is-current-row">
          If intervention&nbsp;
          <el-select
            v-close-on-zoom
            class="is-current-select"
            :value="state.check.isCurrent"
            :disabled="readonly"
            @input="isCurrent => updateCurrent(isCurrent)"
          >
            <el-option :value="false" label="was ever" />
            <el-option :value="true" label="is currently" />
          </el-select>
        </div>
      </div>
      <el-select
        v-close-on-zoom
        :value="state.check.locations"
        placeholder="any location"
        :multiple="true"
        class="location-select"
        :disabled="readonly"
        no-data-text="No location on the body has moulage applied to it"
        @input="(locationKeys) => selectLocations(locationKeys)"
      >
        <el-option
          v-for="location in availableLocations"
          :key="location.key"
          :label="location.label.en"
          :value="location.key"
        />
      </el-select>
      <br>
      <el-select
        v-close-on-zoom
        :value="state.check.category"
        placeholder="Select a category"
        :disabled="readonly"
        class="category-select"
        @input="(categoryKey) => selectCategory(categoryKey)"
      >
        <el-option
          v-for="category in availableCategories"
          :key="category.key"
          :label="category.label.en"
          :value="category.key"
        />
      </el-select>
      <el-select
        v-close-on-zoom
        :value="state.check.treatments"
        placeholder="any treatment"
        :disabled="readonly || !state.check.category"
        :multiple="true"
        class="treatment-select"
        @input="(treatmentKeys) => selectTreatments(treatmentKeys)"
      >
        <el-option
          v-for="treatment in availableTreatments"
          :key="treatment.key"
          :label="treatment.label.en"
          :value="treatment.key"
        />
      </el-select>
      <br>
      <el-select
        v-close-on-zoom
        :value="state.check.types"
        placeholder="any type"
        :disabled="
          readonly ||
            state.check.treatments.length !== 1 ||
            !availableTypes.length
        "
        :multiple="true"
        class="type-select"
        @input="(typeKeys) => selectTypes(typeKeys)"
      >
        <el-option
          v-for="type in availableTypes"
          :key="type.key"
          :label="type.label.en"
          :value="type.key"
        />
      </el-select>
      <el-select
        v-if="availableComponents"
        v-close-on-zoom
        :value="state.check.components"
        placeholder="ignore component"
        :disabled="
          readonly ||
            state.check.types.length !== 1 ||
            !availableComponents.length
        "
        :multiple="true"
        class="component-select"
        @input="(componentKeys) => selectComponents(componentKeys)"
      >
        <el-option
          v-for="component in availableComponents"
          :key="component.key"
          :label="component.label.en"
          :value="component.key"
        />
        <el-option
          label="Any component"
          value="ANY"
          @click.native="selectAnyComponent()"
        >
          <help-tooltip type="info">
            Any component, but one must be selected for this check to pass
          </help-tooltip>
          Any component
        </el-option>
        <el-option
          label="No component"
          value="NONE"
          @click.native="selectNoneComponent()"
        >
          <help-tooltip type="info">
            No component must be selected for this check to pass
          </help-tooltip>
          No component
        </el-option>
        <el-option
          label="Ignore component"
          value="IGNORE"
          @click.native="ignoreComponent()"
        >
          <help-tooltip type="info"> Don't check the component </help-tooltip>
          Ignore component
        </el-option>
      </el-select>
    </div>
    <dosage-count-check
      v-if="showCount"
      mode="COUNT"
      :disabled="readonly"
      :count-available="true"
      :count="state.check.count"
      :count-operator="state.check.countOperator"
      @updateCountOperator="updateCountOperator"
      @updateCount="updateCount"
    />
  </div>
</template>

<script>
import keyBy from 'lodash/keyBy';
import uniq from 'lodash/uniq';
import uniqBy from 'lodash/uniqBy';
import flatten from 'lodash/flatten';

import {
  interventions,
  categoryLabel,
  treatmentLabel,
  typeLabel,
} from '~lib/common/config/interventions';
import HelpTooltip from '~lib/common/components/HelpTooltip';

import DosageCountCheck from './common/DosageCountCheck.vue';

export default {
  name: 'intervention-check',
  components: { DosageCountCheck, HelpTooltip },
  props: [
    'states',
    'readonly',
    'emitter',
    'ikey',
    'getData',
    'putData',
    'control',
  ],
  data() {
    return {
      state: {
        check: {
          category: null,
          treatments: [],
          types: [],
          locations: [],
          components: [],
          count: null,
          countOperator: null,
        },
      },
    };
  },
  computed: {
    availableLocations() {
      const { caseConf } = this.emitter;
      const locations = this.states.find(
        (s) => s.id === 'physicalAssessmentLocations'
      ).possibleValues;
      const enabledInterventionLocationKeys = uniq(
        flatten(
          (caseConf.availableInterventions || []).map((caseIntervention) => {
            const intervention = interventions.find(
              (intv) => intv.key === caseIntervention.key
            );
            return intervention.availableLocations;
          })
        )
      );
      return enabledInterventionLocationKeys.map((locationKey) =>
        locations.find((location) => location.key === locationKey)
      ).filter(location => !!location);
    },
    availableCategories() {
      const categories = uniq(
        this.availableInterventions.map((intv) => intv.category)
      );
      return categories.map((c) => ({
        key: c,
        label: categoryLabel[c],
      }));
    },
    availableTreatments() {
      const treatments = uniq(
        this.availableInterventions
          .filter((intv) => intv.category === this.state.check.category)
          .map((intv) => intv.treatment)
      );
      return treatments.map((t) => ({
        key: t,
        label: treatmentLabel[t],
      }));
    },
    availableTypes() {
      const types = uniq(
        this.availableInterventions
          .filter(
            (intv) =>
              intv.category === this.state.check.category &&
              (this.state.check.treatments || []).includes(intv.treatment)
          )
          .filter((intv) => intv.type)
          .map((intv) => intv.type)
      );
      return types.map((t) => ({
        key: t,
        label: typeLabel[t],
      }));
    },
    availableComponents() {
      const components = uniqBy(
        flatten(
          this.availableInterventions
            .filter(
              (intv) =>
                intv.category === this.state.check.category &&
                (this.state.check.treatments || []).includes(intv.treatment) &&
                (this.state.check.types || []).includes(intv.type)
            )
            .filter((intv) => intv.components)
            .map((intv) => intv.components)
        ),
        'key'
      );

      return components;
    },
    availableInterventions() {
      return (interventions || []).filter((intervention) => {
        const caseIntervention = (
          this.emitter.caseConf.availableInterventions || []
        ).find((caseIntervention) => caseIntervention.key === intervention.key);
        if (!caseIntervention) {
          return false;
        }
        if (!this.state.check.locations || !this.state.check.locations.length) {
          return true;
        }
        return this.state.check.locations.some(location => intervention.availableLocations.includes(location));
      });
    },
    availableInterventionsByKey() {
      return keyBy(this.availableInterventions, 'key');
    },
    selectedInterventions() {
      const { check } = this.state;
      return this.availableInterventions.filter(
        (intervention) =>
          check.category &&
          intervention.category === check.category &&
          (!check.treatments ||
            !check.treatments.length ||
            check.treatments.includes(intervention.treatment)) &&
          (!check.types ||
            !check.types.length ||
            check.types.includes(intervention.type))
      );
    },
    showCount() {
      const { check } = this.state;

      return (
        check.category &&
        check.treatments.length <= 1 &&
        check.types.length <= 1 &&
        this.selectedInterventions.some((intervention) => intervention.multiple)
      );
    },
  },
  created() {
    this.state.check = {
      ...this.state.check,
      ...(this.getData(this.ikey) || {}),
    };
    if (!this.state.check.isCurrent) {
      this.state.check.isCurrent = false;
    }
    this.emitter.trigger('process', this.control);
    this.update();
  },
  methods: {
    selectLocations(locationIds) {
      this.state.check.locations = locationIds;
      if (
        !this.availableInterventions.find(
          (intervention) => intervention.key === this.state.check.id
        )
      ) {
        // Reset the intervention if it's no longer available for location
        this.state.check.id = null;
        this.state.check.components = [];
        this.state.check.count = null;
        this.state.check.countOperator = null;
      }
      this.update();
    },
    selectCategory(categoryKey) {
      this.state.check.category = categoryKey;
      this.state.check.treatments = [];
      this.state.check.types = [];
      this.state.check.components = [];
      this.update();
    },
    selectTreatments(treatmentKeys) {
      this.state.check.treatments = treatmentKeys;
      this.state.check.types = [];
      this.state.check.components = [];
      this.update();
    },
    selectTypes(typeKeys) {
      this.state.check.types = typeKeys;
      this.state.check.components = [];
      this.update();
    },
    selectComponents(componentKeys) {
      this.state.check.components = componentKeys.filter(
        (componentKey) => !['ANY', 'NONE', 'IGNORE'].includes(componentKey)
      );
      this.update();
    },
    selectAnyComponent() {
      this.state.check.components = ['ANY'];
      this.update();
    },
    selectNoneComponent() {
      this.state.check.components = ['NONE'];
      this.update();
    },
    ignoreComponent() {
      this.state.check.components = [];
      this.update();
    },
    updateCount(count) {
      this.state.check.count = count;
      this.update();
    },
    updateCountOperator(countOperator) {
      this.state.check.countOperator = countOperator;
      this.update();
    },
    updateCurrent(value) {
      this.state.check.isCurrent = value;
      this.update();
    },
    moulageAtLocation(location) {
      const { caseConf } = this.emitter;
      if (caseConf.moulages && caseConf.moulages[location]) {
        return caseConf.moulages[location];
      } else if (
        caseConf.structuralMoulages &&
        caseConf.structuralMoulages[location]
      ) {
        return caseConf.structuralMoulages[location];
      }
    },
    update() {
      if (!this.showCount) {
        this.state.check.count = null;
        this.state.check.countOperator = null;
      }
      this.putData(this.ikey, {
        ...this.state.check,
        type: 'INTERVENTION_ADMINISTERED',
      });
      this.emitter.trigger('process', this.control);
      this.$forceUpdate();
    },
  },
};
</script>

<style scoped>
.location-select {
  width: 100%;
}
.is-current-row {
  width: 200px;
  display: flex;
}
.is-current-select {
  color: black;
}
</style>
